import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Spinner,
  Alert,
  Badge,
  Progress,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  InputGroup,
  InputGroupText,
  Table,
  Button,
  ButtonGroup
} from "reactstrap"
import Chart from "react-apexcharts"
import {
  getRFQGoodServiceAnalytics,
  getRFQGoodServiceDetails,
  exportGoodServiceDetails,
  formatCurrency
} from "../../../../helpers/rfq_dashboard_api"
import InfoButton from "./InfoButton"

const GoodServiceAnalytics = ({ filters }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState('')
  const [modalTitle, setModalTitle] = useState('')
  const [modalData, setModalData] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalPage, setModalPage] = useState(1)
  const [modalPerPage, setModalPerPage] = useState(10)
  const [modalSearch, setModalSearch] = useState('')

  const [modalAdditionalParams, setModalAdditionalParams] = useState({})
  const [exportLoading, setExportLoading] = useState(false)

  // Nested modal for RFQ list
  const [rfqModalOpen, setRfqModalOpen] = useState(false)
  const [rfqModalTitle, setRfqModalTitle] = useState('')
  const [rfqModalData, setRfqModalData] = useState(null)
  const [rfqModalLoading, setRfqModalLoading] = useState(false)
  const [rfqModalPage, setRfqModalPage] = useState(1)
  const [rfqModalPerPage, setRfqModalPerPage] = useState(10)
  const [rfqModalSearch, setRfqModalSearch] = useState('')

  // Track current RFQ modal context for pagination/search
  const [rfqModalType, setRfqModalType] = useState('')
  const [rfqModalServiceId, setRfqModalServiceId] = useState(null)



  // Load Good and Service analytics data
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      console.log("Loading good service analytics with filters:", filters)
      
      const response = await getRFQGoodServiceAnalytics({
        ...filters
      })

      console.log("Good service analytics response:", response)

      if (response && response.status) {
        setData(response.data)
        console.log("Good service analytics loaded successfully:", response.data)
      } else {
        throw new Error(response?.message || "Failed to load good service analytics")
      }
    } catch (err) {
      console.error("Good service analytics loading error:", err)
      setError(err.message || "Failed to load good service analytics")
    } finally {
      setLoading(false)
    }
  }

  // Handle card/chart click to open modal
  const handleCardClick = (type, title, additionalParams = {}) => {
    try {
      console.log("Card/Chart clicked:", { type, title, additionalParams })
      setModalType(type)
      setModalTitle(title)
      setModalPage(1)
      setModalSearch('')

      setModalAdditionalParams(additionalParams)
      setModalOpen(true)
      loadModalData(type, 1, '', additionalParams)
    } catch (error) {
      console.error("Error handling card click:", error)
    }
  }

  // Load modal data
  const loadModalData = async (type, page = 1, search = '', additionalParams = {}, customPerPage = null) => {
    try {
      setModalLoading(true)

      const actualPerPage = customPerPage || modalPerPage;

      console.log("=== LOAD MODAL DATA DEBUG ===")
      console.log("Type:", type)
      console.log("Page:", page)
      console.log("Search:", search)
      console.log("CustomPerPage parameter:", customPerPage)
      console.log("Current modalPerPage state:", modalPerPage)
      console.log("ActualPerPage (final value):", actualPerPage)
      console.log("AdditionalParams:", additionalParams)

      const params = {
        type: type,
        page: page,
        per_page: actualPerPage, // Use customPerPage if provided, otherwise use state
        search: search,
        _timestamp: Date.now(), // Add timestamp to prevent caching
        ...filters,
        ...additionalParams
      }

      // Double-check per_page value is correct
      if (customPerPage) {
        params.per_page = customPerPage;
        console.log("CUSTOM PER_PAGE OVERRIDE - Setting per_page to:", customPerPage);
      }

      console.log("=== FINAL API PARAMS ===")
      console.log("Full params object:", JSON.stringify(params, null, 2))

      // Call backend API for modal data
      const response = await getRFQGoodServiceDetails(params)

      console.log("=== API RESPONSE DEBUG ===")
      console.log("Response status:", response?.status)
      console.log("Response data:", response?.data)
      console.log("Response pagination:", response?.data?.pagination)
      console.log("Records count:", response?.data?.records?.length)

      if (response && response.status) {
        setModalData(response.data)
        console.log("=== MODAL DATA SET SUCCESSFULLY ===")
        console.log("Pagination info:", response.data.pagination)
        console.log("Per page from response:", response.data.pagination?.per_page)
        console.log("Total records:", response.data.pagination?.total_records)
      } else {
        console.error("Modal data response error:", response)
        throw new Error(response?.message || "Failed to load modal data")
      }
    } catch (err) {
      console.error("Modal data loading error:", err)
      setModalData({
        records: [],
        columns: ['Error'],
        pagination: {
          current_page: 1,
          per_page: customPerPage || modalPerPage,
          total_records: 0,
          total_pages: 0,
          has_next_page: false,
          has_prev_page: false
        }
      })
    } finally {
      setModalLoading(false)
    }
  }

  // Handle modal pagination
  const handleModalPageChange = (page) => {
    setModalPage(page)
    loadModalData(modalType, page, modalSearch, modalAdditionalParams)
  }

  // Handle modal search - exact same as Overview tab
  const handleModalSearch = (searchTerm) => {
    setModalSearch(searchTerm)
    setModalPage(1)
    loadModalData(modalType, 1, searchTerm, modalAdditionalParams)
  }

  // Handle modal per page change
  const handleModalPerPageChange = (perPage) => {
    console.log("=== MODAL PER PAGE CHANGE DEBUG ===")
    console.log("handleModalPerPageChange called with:", perPage)
    console.log("Current modalPerPage before setState:", modalPerPage)
    console.log("Current modalType:", modalType)
    console.log("Current modalAdditionalParams:", modalAdditionalParams)

    // Update state immediately
    setModalPerPage(perPage)
    setModalPage(1)

    // Force a small delay to ensure state updates are processed
    setTimeout(() => {
      console.log("Loading modal data with forced perPage:", perPage)
      // Pass the new perPage value directly to avoid state update delay
      loadModalData(modalType, 1, modalSearch, modalAdditionalParams, perPage)
    }, 50) // 50ms delay to ensure state updates
  }

  // Handle modal export
  const handleModalExport = async () => {
    try {
      setExportLoading(true)

      const params = {
        type: modalType,
        search: modalSearch,
        ...filters,
        ...modalAdditionalParams
      }

      console.log("Exporting with params:", params)

      // Call export API
      await exportGoodServiceDetails(params)

      console.log("Export completed successfully")

    } catch (error) {
      console.error("Export failed:", error)
      alert("Export failed: " + error.message)
    } finally {
      setExportLoading(false)
    }
  }

  // Handle View RFQs button click for service heads
  const handleViewRFQs = (serviceHeadId, serviceHeadName) => {
    try {
      console.log("View RFQs clicked:", { serviceHeadId, serviceHeadName })
      setRfqModalTitle(`RFQs for ${serviceHeadName}`)
      setRfqModalPage(1)
      setRfqModalSearch('')

      setRfqModalType('service_head_rfqs')
      setRfqModalServiceId(serviceHeadId)
      setRfqModalOpen(true)
      loadRfqModalData('service_head_rfqs', serviceHeadId, 1, '')
    } catch (error) {
      console.error("Error opening RFQ modal:", error)
    }
  }

  // Handle View RFQs button click for service subheads
  const handleViewSubheadRFQs = (serviceSubheadId, serviceSubheadName) => {
    try {
      console.log("View Subhead RFQs clicked:", { serviceSubheadId, serviceSubheadName })
      setRfqModalTitle(`RFQs for ${serviceSubheadName}`)
      setRfqModalPage(1)
      setRfqModalSearch('')

      setRfqModalType('service_subhead_rfqs')
      setRfqModalServiceId(serviceSubheadId)
      setRfqModalOpen(true)
      loadRfqModalData('service_subhead_rfqs', serviceSubheadId, 1, '')
    } catch (error) {
      console.error("Error opening Subhead RFQ modal:", error)
    }
  }

  // Load RFQ modal data
  const loadRfqModalData = async (type, serviceId, page = 1, search = '', customPerPage = null) => {
    try {
      setRfqModalLoading(true)

      const params = {
        type: type,
        page: page,
        per_page: customPerPage || rfqModalPerPage, // Use customPerPage if provided, otherwise use state
        search: search,
        _timestamp: Date.now(), // Add timestamp to prevent caching
        ...filters
      }

      // Add the appropriate ID parameter based on type
      if (type === 'service_head_rfqs') {
        params.service_head_id = serviceId
      } else if (type === 'service_subhead_rfqs') {
        params.service_subhead_id = serviceId
      }

      console.log("Loading RFQ modal data with params:", params)

      const response = await getRFQGoodServiceDetails(params)

      console.log("RFQ modal data response:", response)

      if (response && response.status) {
        setRfqModalData(response.data)
        console.log("RFQ modal data set successfully:", response.data)
      } else {
        console.error("RFQ modal data response error:", response)
        throw new Error(response?.message || "Failed to load RFQ data")
      }
    } catch (err) {
      console.error("RFQ modal data loading error:", err)
      setRfqModalData({
        records: [],
        columns: ['Error'],
        pagination: {
          current_page: 1,
          per_page: customPerPage || rfqModalPerPage,
          total_records: 0,
          total_pages: 0,
          has_next_page: false,
          has_prev_page: false
        }
      })
    } finally {
      setRfqModalLoading(false)
    }
  }

  // Load RFQ modal data with specific per page value (for per page changes)
  const loadRfqModalDataWithPerPage = async (type, serviceId, page = 1, search = '', perPage) => {
    try {
      setRfqModalLoading(true)

      const params = {
        type: type,
        page: page,
        per_page: perPage, // Use the passed perPage value instead of state
        search: search,
        ...filters
      }

      // Add the appropriate ID parameter based on type
      if (type === 'service_head_rfqs') {
        params.service_head_id = serviceId
      } else if (type === 'service_subhead_rfqs') {
        params.service_subhead_id = serviceId
      }

      console.log("Loading RFQ modal data with specific per_page:", params)

      const response = await getRFQGoodServiceDetails(params)

      console.log("RFQ modal data response:", response)

      if (response && response.status) {
        setRfqModalData(response.data)
        console.log("RFQ modal data set successfully:", response.data)
      } else {
        console.error("RFQ modal data response error:", response)
        throw new Error(response?.message || "Failed to load RFQ data")
      }
    } catch (err) {
      console.error("RFQ modal data loading error:", err)
      setRfqModalData({
        records: [],
        columns: ['Error'],
        pagination: {
          current_page: 1,
          per_page: perPage, // Use the passed perPage value
          total_records: 0,
          total_pages: 0,
          has_next_page: false,
          has_prev_page: false
        }
      })
    } finally {
      setRfqModalLoading(false)
    }
  }

  // Handle RFQ modal search - exact same as Overview tab
  const handleRfqModalSearch = (searchTerm) => {
    setRfqModalSearch(searchTerm)
    setRfqModalPage(1)
    if (rfqModalType && rfqModalServiceId) {
      loadRfqModalData(rfqModalType, rfqModalServiceId, 1, searchTerm)
    }
  }

  // Handle RFQ modal pagination
  const handleRfqModalPageChange = (newPage) => {
    setRfqModalPage(newPage)
    if (rfqModalType && rfqModalServiceId) {
      loadRfqModalData(rfqModalType, rfqModalServiceId, newPage, rfqModalSearch)
    }
  }

  // Handle RFQ modal per page change
  const handleRfqModalPerPageChange = (newPerPage) => {
    setRfqModalPerPage(newPerPage)
    setRfqModalPage(1)
    if (rfqModalType && rfqModalServiceId) {
      // Pass the new perPage value directly to avoid state update delay
      loadRfqModalData(rfqModalType, rfqModalServiceId, 1, rfqModalSearch, newPerPage)
    }
  }

  // Load data when filters change
  useEffect(() => {
    loadData()
  }, [filters])

  // Debug useEffect to monitor modalPerPage changes
  useEffect(() => {
    console.log("=== MODAL PER PAGE STATE CHANGED ===")
    console.log("New modalPerPage value:", modalPerPage)
    console.log("Modal is open:", modalOpen)
    console.log("Modal type:", modalType)
  }, [modalPerPage])

  // Debug useEffect to monitor modal data changes
  useEffect(() => {
    if (modalData && modalData.pagination) {
      console.log("=== MODAL DATA UPDATED ===")
      console.log("Pagination per_page:", modalData.pagination.per_page)
      console.log("Current modalPerPage state:", modalPerPage)
      console.log("Records count:", modalData.records?.length)

      // Check for mismatch between state and response
      if (modalData.pagination.per_page !== modalPerPage) {
        console.warn("MISMATCH DETECTED!")
        console.warn("State modalPerPage:", modalPerPage)
        console.warn("Response per_page:", modalData.pagination.per_page)
      }
    }
  }, [modalData, modalPerPage])



  // Extract data for easier access
  const {
    summary_cards = {},
    category_distribution = [],
    head_performance = [],
    subhead_trends = [],
    rfq_by_service = [],
    budget_by_service_head = [],
    payment_by_service_head = []
  } = data || {}



  // Show loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <Spinner color="primary" />
          <div className="mt-3">
            <h5>Loading Good & Service Analytics...</h5>
            <p className="text-muted">Please wait while we analyze the data</p>
          </div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <Alert color="danger" className="text-center">
        <h4 className="alert-heading">Error Loading Good & Service Analytics</h4>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadData}>
          <i className="bx bx-refresh me-1"></i>
          Try Again
        </button>
      </Alert>
    )
  }

  return (
    <React.Fragment>
      <style>{`
        .cursor-pointer {
          cursor: pointer !important;
        }
        .hover-highlight:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }
        .metric-card {
          transition: all 0.3s ease;
        }
        .dashboard-card {
          transition: all 0.3s ease;
        }
      `}</style>
      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={6}>
          <Card
            className="metric-card success cursor-pointer hover-highlight"
            onClick={() => handleCardClick('service_heads', 'Service Heads Details')}
            title="Click to view service heads details"
            style={{ position: 'relative' }}
          >
            <CardBody className="text-center">
              <div className="metric-value text-success">{summary_cards?.total_heads || 0}</div>
              <div className="metric-label">Service Heads</div>
              <small className="text-success">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </CardBody>
          </Card>
        </Col>
        <Col md={6}>
          <Card
            className="metric-card warning cursor-pointer hover-highlight"
            onClick={() => handleCardClick('service_subheads', 'Service Subheads Details')}
            title="Click to view service subheads details"
            style={{ position: 'relative' }}
          >
            <CardBody className="text-center">
              <div className="metric-value text-warning">{summary_cards?.total_subheads || 0}</div>
              <div className="metric-label">Service Subheads</div>
              <small className="text-warning">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Category Distribution and Payment Status Charts */}
      {!loading && data && (
        <Row className="mb-4">
          <Col lg={6}>
            <Card className="dashboard-card">
              <CardBody>
                <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bx bx-pie-chart-alt me-2"></i>
                    Category Distribution
                  </span>
                  <div className="d-flex align-items-center">
                    <InfoButton
                      type="good_service_category"
                      title="Category Distribution - RFQ Count by Service Category"
                      size="sm"
                    />
                    <small className="text-muted ms-2">
                      <i className="bx bx-mouse me-1"></i>Click on segments for details
                    </small>
                  </div>
                </CardTitle>
                {category_distribution && category_distribution.length > 0 ? (
                  <div key={`category-chart-${category_distribution.length}`}>
                    <Chart
                      key={`category-chart-instance-${category_distribution.length}`}
                      options={{
                        chart: {
                          id: `category-distribution-${Date.now()}`,
                          type: 'donut',
                          height: 400,
                          fontFamily: 'inherit',
                          offsetX: 30,  // Shift chart right to balance with left legend
                          toolbar: {
                            show: false
                          },
                          events: {
                            dataPointSelection: function(event, chartContext, config) {
                              try {
                                if (config && config.dataPointIndex !== undefined && category_distribution[config.dataPointIndex]) {
                                  const selectedCategory = category_distribution[config.dataPointIndex]?.category_name
                                  if (selectedCategory) {
                                    handleCardClick('category_rfqs', `RFQs for ${selectedCategory}`, { category_name: selectedCategory })
                                  }
                                }
                              } catch (error) {
                                console.error('Chart click error:', error)
                              }
                            }
                          }
                        },
                        labels: category_distribution.map(item => item.category_name || 'Unknown'),
                        colors: ['#556ee6', '#34c38f', '#f1b44c', '#e83e8c', '#50a5f1', '#f46a6a', '#74788d', '#f34e4e'],
                        legend: {
                          position: 'left',
                          offsetX: -10,
                          offsetY: 0,
                          fontSize: '12px',
                          fontFamily: 'inherit',
                          fontWeight: 500,
                          markers: {
                            width: 8,
                            height: 8,
                            radius: 4
                          },
                          itemMargin: {
                            horizontal: 5,
                            vertical: 8
                          },
                          formatter: function(seriesName, opts) {
                            // Show full category names in legend
                            const categoryData = category_distribution[opts.seriesIndex]
                            return categoryData?.category_name || 'Unknown'
                          },
                          width: 180,
                          height: undefined
                        },
                        tooltip: {
                          enabled: true,
                          style: {
                            fontSize: '14px',
                            fontFamily: 'inherit'
                          },
                          y: {
                            formatter: function(val, opts) {
                              const categoryData = category_distribution[opts.seriesIndex]
                              const totalBudget = categoryData?.total_budget || 0

                              // Show only data without duplicate category name
                              return `<div style="padding: 6px;">
                                <div style="font-size: 13px;">
                                  <span style="color: #556ee6;">●</span> ${val} RFQs<br/>
                                  <span style="color: #34c38f;">●</span> ${formatCurrency(totalBudget)}
                                </div>
                                <div style="font-size: 11px; color: #74788d; margin-top: 4px;">
                                  Click for details
                                </div>
                              </div>`
                            }
                          }
                        },
                        plotOptions: {
                          pie: {
                            donut: {
                              size: '65%',
                              labels: {
                                show: true,
                                name: {
                                  show: true,
                                  fontSize: '16px',
                                  fontFamily: 'inherit',
                                  fontWeight: 600,
                                  color: '#373d3f',
                                  offsetY: -10
                                },
                                value: {
                                  show: true,
                                  fontSize: '24px',
                                  fontFamily: 'inherit',
                                  fontWeight: 700,
                                  color: '#556ee6',
                                  offsetY: 10,
                                  formatter: function(val) {
                                    return val + ' RFQs'
                                  }
                                },
                                total: {
                                  show: true,
                                  showAlways: true,
                                  label: 'Total RFQs',
                                  fontSize: '14px',
                                  fontFamily: 'inherit',
                                  fontWeight: 500,
                                  color: '#74788d',
                                  formatter: function(w) {
                                    const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                                    return total
                                  }
                                }
                              }
                            }
                          }
                        },
                        dataLabels: {
                          enabled: false
                        },
                        states: {
                          hover: {
                            filter: {
                              type: 'none'
                            }
                          },
                          active: {
                            allowMultipleDataPointsSelection: false,
                            filter: {
                              type: 'none'
                            }
                          }
                        },
                        responsive: [{
                          breakpoint: 768,
                          options: {
                            chart: {
                              height: 350
                            },
                            legend: {
                              position: 'bottom',
                              fontSize: '11px',
                              itemMargin: {
                                horizontal: 4,
                                vertical: 2
                              },
                              formatter: function(seriesName, opts) {
                                // For tablet, show full category names
                                const categoryData = category_distribution[opts.seriesIndex]
                                return categoryData?.category_name || 'Unknown'
                              }
                            },
                            plotOptions: {
                              pie: {
                                donut: {
                                  size: '70%',
                                  labels: {
                                    show: true,
                                    name: {
                                      fontSize: '14px'
                                    },
                                    value: {
                                      fontSize: '20px'
                                    },
                                    total: {
                                      fontSize: '12px'
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }, {
                          breakpoint: 480,
                          options: {
                            chart: {
                              height: 300
                            },
                            legend: {
                              position: 'bottom',
                              fontSize: '10px',
                              itemMargin: {
                                horizontal: 3,
                                vertical: 2
                              },
                              formatter: function(seriesName, opts) {
                                // For mobile, show full category names (may wrap)
                                const categoryData = category_distribution[opts.seriesIndex]
                                return categoryData?.category_name || 'Unknown'
                              }
                            },
                            plotOptions: {
                              pie: {
                                donut: {
                                  size: '75%',
                                  labels: {
                                    show: true,
                                    name: {
                                      fontSize: '12px'
                                    },
                                    value: {
                                      fontSize: '18px'
                                    },
                                    total: {
                                      fontSize: '11px'
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }]
                      }}
                      series={category_distribution.map(item => parseInt(item.rfq_count) || 0)}
                      type="donut"
                      height={400}
                    />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bx bx-pie-chart-alt display-4 text-muted"></i>
                    <p className="text-muted mt-2">No category data available</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="dashboard-card">
              <CardBody>
                <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bx bx-credit-card me-2"></i>
                    Paid vs Final Amount (Per Service Head)
                  </span>
                  <div className="d-flex align-items-center">
                    <InfoButton
                      type="good_service_payment"
                      title="Payment Status Analysis - Paid vs Final Amount by Service Head"
                      size="sm"
                    />
                    <small className="text-muted ms-2">
                      <i className="bx bx-mouse me-1"></i>Click on bars for details
                    </small>
                  </div>
                </CardTitle>
                {payment_by_service_head && payment_by_service_head.length > 0 ? (
                  <div key={`payment-chart-${payment_by_service_head.length}`} className="payment-chart-container">
                    <Chart
                      key={`payment-chart-instance-${payment_by_service_head.length}`}
                      options={{
                        chart: {
                          id: `payment-analysis-${Date.now()}`,
                          type: 'bar',
                          height: 450,
                          fontFamily: 'inherit',
                          stacked: true,
                          toolbar: {
                            show: false
                          },
                          zoom: {
                            enabled: false
                          },
                          offsetY: -10,
                          events: {
                            dataPointSelection: function(event, chartContext, config) {
                              try {
                                if (config && config.dataPointIndex !== undefined && payment_by_service_head[config.dataPointIndex]) {
                                  const selectedServiceHead = payment_by_service_head[config.dataPointIndex]?.service_head_name
                                  if (selectedServiceHead) {
                                    if (config.seriesIndex === 0) {
                                      // Click on Final Amount stack - show final amount and budget details
                                      const modalTitle = `Final Amount & Budget Details for ${selectedServiceHead}`
                                      handleCardClick('payment_service_head_rfqs', modalTitle, {
                                        service_head_name: selectedServiceHead,
                                        payment_status: 'unpaid'
                                      })
                                    } else if (config.seriesIndex === 1) {
                                      // Click on Paid Amount stack - show paid amount history
                                      const modalTitle = `Paid Amount History for ${selectedServiceHead}`
                                      handleCardClick('payment_service_head_rfqs', modalTitle, {
                                        service_head_name: selectedServiceHead,
                                        payment_status: 'paid'
                                      })
                                    }
                                  }
                                }
                              } catch (error) {
                                console.error('Payment chart click error:', error)
                              }
                            }
                          }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: '55%',
                            endingShape: 'rounded'
                          }
                        },
                        dataLabels: {
                          enabled: true,
                          style: {
                            fontSize: '10px',
                            fontWeight: 'bold',
                            colors: ['#fff']
                          },
                          formatter: function(val, opts) {
                            if (val > 0) {
                              return '₹' + val.toLocaleString()
                            }
                            return ''
                          }
                        },
                        stroke: {
                          show: true,
                          width: 2,
                          colors: ['transparent']
                        },
                        xaxis: {
                          categories: payment_by_service_head.map(item => item.service_head_name || 'Unknown'),
                          labels: {
                            rotate: -15,
                            style: {
                              fontSize: '11px',
                              fontFamily: 'inherit',
                              fontWeight: 500,
                              colors: '#74788d'
                            },
                            maxHeight: 80,
                            trim: false,
                            hideOverlappingLabels: false,
                            offsetY: 5
                          },
                          axisBorder: {
                            show: true,
                            color: '#e9ecef',
                            height: 1,
                            width: '100%',
                            offsetX: 0,
                            offsetY: 0
                          },
                          axisTicks: {
                            show: true,
                            borderType: 'solid',
                            color: '#e9ecef',
                            height: 6,
                            offsetX: 0,
                            offsetY: 0
                          }
                        },
                        yaxis: {
                          title: {
                            text: 'Amount (₹)',
                            style: {
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              fontWeight: 600,
                              color: '#495057'
                            }
                          },
                          labels: {
                            style: {
                              fontSize: '12px',
                              fontFamily: 'inherit',
                              colors: '#74788d'
                            },
                            formatter: function(val) {
                              return '₹' + val.toLocaleString()
                            }
                          },
                          min: 0,
                          forceNiceScale: true
                        },
                        fill: {
                          opacity: 1
                        },
                        tooltip: {
                          shared: true,
                          intersect: false,
                          style: {
                            fontSize: '14px',
                            fontFamily: 'inherit'
                          },
                          custom: function({series, seriesIndex, dataPointIndex, w}) {
                            const serviceHeadData = payment_by_service_head[dataPointIndex]
                            if (!serviceHeadData) return ''

                            const totalRFQs = parseInt(serviceHeadData.total_rfqs) || 0
                            const paidAmount = parseFloat(serviceHeadData.paid_amount) || 0
                            const finalAmount = parseFloat(serviceHeadData.final_amount) || 0

                            return `
                              <div class="custom-tooltip p-3">
                                <div class="tooltip-title mb-2"><strong>${serviceHeadData.service_head_name || 'Unknown'}</strong></div>
                                <div class="tooltip-content">
                                  <div class="d-flex justify-content-between mb-1">
                                    <span>Total RFQs:</span>
                                    <strong class="text-primary">${totalRFQs}</strong>
                                  </div>
                                  <div class="d-flex justify-content-between mb-1">
                                    <span>Paid Amount:</span>
                                    <strong class="text-success">₹${paidAmount.toLocaleString()}</strong>
                                  </div>
                                  <div class="d-flex justify-content-between">
                                    <span>Final Amount:</span>
                                    <strong class="text-info">₹${finalAmount.toLocaleString()}</strong>
                                  </div>
                                </div>
                              </div>
                            `
                          }
                        },
                        colors: ['#007bff', '#28a745'], // Blue for Final Amount, Green for Paid
                        legend: {
                          position: 'top',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          fontWeight: 500,
                          markers: {
                            width: 12,
                            height: 12,
                            radius: 6
                          }
                        },
                        grid: {
                          borderColor: '#f1f1f1',
                          strokeDashArray: 3
                        },
                        responsive: [{
                          breakpoint: 768,
                          options: {
                            chart: {
                              height: 400
                            },
                            xaxis: {
                              labels: {
                                rotate: -45,
                                fontSize: '10px',
                                maxHeight: 100,
                                offsetY: 10
                              }
                            }
                          }
                        }, {
                          breakpoint: 480,
                          options: {
                            chart: {
                              height: 350
                            },
                            xaxis: {
                              labels: {
                                rotate: -45,
                                fontSize: '9px',
                                maxHeight: 120,
                                offsetY: 15
                              }
                            }
                          }
                        }]
                      }}
                      series={[
                        {
                          name: 'Final Amount',
                          data: payment_by_service_head.map(item => {
                            const finalAmount = parseFloat(item.final_amount) || 0
                            const paidAmount = parseFloat(item.paid_amount) || 0
                            return Math.max(0, finalAmount - paidAmount)
                          }),
                          color: '#007bff'
                        },
                        {
                          name: 'Paid Amount',
                          data: payment_by_service_head.map(item => parseFloat(item.paid_amount) || 0),
                          color: '#28a745'
                        }
                      ]}
                      type="bar"
                      height={400}
                    />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bx bx-credit-card display-4 text-muted"></i>
                    <p className="text-muted mt-2">No payment vs final amount data available</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Top Service Heads Performance Chart */}
      {!loading && data && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="dashboard-card">
              <CardBody>
                <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bx bx-bar-chart-alt-2 me-2"></i>
                    Top Service Heads Performance
                  </span>
                  <div className="d-flex align-items-center">
                    <InfoButton
                      type="good_service_heads"
                      title="Service Heads Performance - RFQ Count and Completion Rate"
                      size="sm"
                    />
                    <small className="text-muted ms-2">
                      <i className="bx bx-mouse me-1"></i>Click on bars for details
                    </small>
                  </div>
                </CardTitle>
                {head_performance && head_performance.length > 0 ? (
                  <div key={`heads-chart-${head_performance.length}`}>
                    <Chart
                      key={`heads-chart-instance-${head_performance.length}`}
                      options={{
                        chart: {
                          id: `heads-performance-${Date.now()}`,
                          type: 'bar',
                          height: 400,
                          width: '100%',
                          fontFamily: 'inherit',
                          toolbar: {
                            show: false
                          },
                          zoom: {
                            enabled: false
                          },
                          animations: {
                            enabled: true
                          },
                          offsetX: head_performance.length <= 2 ? 0 : 0,
                          offsetY: 0,
                          events: {
                            dataPointSelection: function(event, chartContext, config) {
                              try {
                                if (config && config.dataPointIndex !== undefined && head_performance[config.dataPointIndex]) {
                                  const selectedHead = head_performance[config.dataPointIndex]?.head_text
                                  const seriesIndex = config.seriesIndex
                                  if (selectedHead) {
                                    let statusFilter = 'total'
                                    let modalTitle = `RFQs for ${selectedHead}`

                                    if (seriesIndex === 0) { // Total RFQs (orange bar)
                                      statusFilter = 'total'
                                      modalTitle = `Total RFQs for ${selectedHead}`
                                    } else if (seriesIndex === 1) { // Assigned RFQs (blue bar)
                                      statusFilter = 'assigned'
                                      modalTitle = `Assigned RFQs for ${selectedHead}`
                                    } else if (seriesIndex === 2) { // Closed RFQs (green bar)
                                      statusFilter = 'closed'
                                      modalTitle = `Closed RFQs for ${selectedHead}`
                                    }

                                    handleCardClick('head_rfqs', modalTitle, {
                                      head_text: selectedHead,
                                      status_filter: statusFilter
                                    })
                                  }
                                }
                              } catch (error) {
                                console.error('Chart click error:', error)
                              }
                            }
                          }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: head_performance.length === 1 ? '30%' : head_performance.length === 2 ? '40%' : '55%',
                            endingShape: 'rounded',
                            distributed: false,
                            rangeBarOverlap: true,
                            rangeBarGroupRows: false
                          }
                        },
                        dataLabels: {
                          enabled: false
                        },
                        stroke: {
                          show: true,
                          width: 2,
                          colors: ['transparent']
                        },
                        xaxis: {
                          categories: head_performance.map(item => item.head_text || 'Unknown'),
                          labels: {
                            rotate: 0,  // Remove rotation for better readability
                            style: {
                              fontSize: '11px',
                              fontFamily: 'inherit',
                              fontWeight: 500,
                              colors: '#74788d'
                            },
                            maxHeight: 60,
                            trim: true,
                            hideOverlappingLabels: false
                          },
                          axisBorder: {
                            show: true
                          },
                          axisTicks: {
                            show: true
                          },
                          tickPlacement: 'on'
                          // Removed title to eliminate "Service Heads" text
                        },
                        yaxis: {
                          title: {
                            text: 'Count',
                            style: {
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              fontWeight: 600,
                              color: '#495057'
                            }
                          },
                          labels: {
                            style: {
                              fontSize: '12px',
                              fontFamily: 'inherit',
                              colors: '#74788d'
                            },
                            formatter: function(val) {
                              return Math.floor(val)
                            }
                          },
                          min: 0,
                          max: undefined,
                          forceNiceScale: true,
                          decimalsInFloat: 0
                        },
                        fill: {
                          opacity: 1
                        },
                        tooltip: {
                          shared: false,
                          intersect: true,
                          style: {
                            fontSize: '14px',
                            fontFamily: 'inherit'
                          },
                          y: {
                            formatter: function(val, opts) {
                              const seriesName = opts.series[opts.seriesIndex].name
                              return `${val} RFQs`
                            }
                          },
                          custom: function({series, seriesIndex, dataPointIndex, w}) {
                            const headData = head_performance[dataPointIndex]
                            const seriesNames = ['Total RFQs', 'Assigned RFQs', 'Closed RFQs']
                            const colors = ['#f1b44c', '#50a5f1', '#34c38f']
                            const values = [
                              headData?.rfq_count || 0,
                              headData?.assigned_count || 0,
                              headData?.closed_count || 0
                            ]

                            return `<div class="apexcharts-tooltip-custom" style="padding: 8px; background: white; border: 1px solid #e3e6f0; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                              <div style="font-weight: 600; margin-bottom: 4px; color: #5a5c69;">${headData?.head_text || 'Service Head'}</div>
                              <div style="font-size: 12px;">
                                <div style="margin: 2px 0;"><span style="color: ${colors[0]};">●</span> Total: ${values[0]} RFQs</div>
                                <div style="margin: 2px 0;"><span style="color: ${colors[1]};">●</span> Assigned: ${values[1]} RFQs</div>
                                <div style="margin: 2px 0;"><span style="color: ${colors[2]};">●</span> Closed: ${values[2]} RFQs</div>
                              </div>
                            </div>`
                          }
                        },
                        colors: ['#f1b44c', '#50a5f1', '#34c38f'],
                        legend: {
                          position: 'top',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          fontWeight: 500,
                          markers: {
                            width: 12,
                            height: 12,
                            radius: 6
                          }
                        },

                        states: {
                          hover: {
                            filter: {
                              type: 'none'
                            }
                          },
                          active: {
                            allowMultipleDataPointsSelection: false,
                            filter: {
                              type: 'none'
                            }
                          }
                        },
                        dataLabels: {
                          enabled: false
                        },
                        grid: {
                          borderColor: '#f1f1f1',
                          strokeDashArray: 3,
                          padding: {
                            top: 0,
                            right: head_performance.length <= 2 ? 80 : 20,
                            bottom: 0,
                            left: head_performance.length <= 2 ? 80 : 20
                          }
                        },
                        responsive: [{
                          breakpoint: 768,
                          options: {
                            chart: {
                              height: 350
                            },
                            xaxis: {
                              categories: head_performance.map(item => item.head_text || 'Unknown'),
                              labels: {
                                rotate: -45,  // Rotate labels for better fit on tablet
                                fontSize: '10px',
                                hideOverlappingLabels: true,
                                maxHeight: 100
                              }
                            }
                          }
                        }, {
                          breakpoint: 480,
                          options: {
                            chart: {
                              height: 300
                            },
                            xaxis: {
                              categories: head_performance.map(item => item.head_text || 'Unknown'),
                              labels: {
                                rotate: -45,  // Rotate labels for better fit on mobile
                                fontSize: '9px',
                                hideOverlappingLabels: true,
                                maxHeight: 80
                              }
                            }
                          }
                        }]
                      }}
                      series={[
                        {
                          name: 'Total RFQs',
                          data: head_performance.map(item => parseInt(item.rfq_count) || 0)
                        },
                        {
                          name: 'Assigned RFQs',
                          data: head_performance.map(item => parseInt(item.assigned_count) || 0)
                        },
                        {
                          name: 'Closed RFQs',
                          data: head_performance.map(item => parseInt(item.closed_count) || 0)
                        }
                      ]}
                      type="bar"
                      height={400}
                    />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bx bx-bar-chart-alt-2 display-4 text-muted"></i>
                    <p className="text-muted mt-2">No head performance data available</p>

                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Budget Analysis */}
      {!loading && data && (
        <Row className="mb-4">
          <Col lg={12}>
            <Card className="dashboard-card">
              <CardBody>
                <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bx bx-trending-up me-2"></i>
                    Budget vs Final Amount (Per Service Head)
                  </span>
                  <div className="d-flex align-items-center">
                    <InfoButton
                      type="good_service_budget"
                      title="Budget vs Final Amount Analysis - Comparison of budgeted vs actual final amounts by Service Head"
                      size="sm"
                    />
                    <small className="text-muted ms-2">
                      <i className="bx bx-mouse me-1"></i>Click on bars for details
                    </small>
                  </div>
                </CardTitle>
                {budget_by_service_head && budget_by_service_head.length > 0 ? (
                  <div key={`budget-chart-${budget_by_service_head.length}`}>
                    <Chart
                      key={`budget-chart-instance-${budget_by_service_head.length}`}
                      options={{
                        chart: {
                          id: `budget-vs-final-analysis-${Date.now()}`,
                          type: 'bar',
                          height: 400,
                          fontFamily: 'inherit',
                          toolbar: {
                            show: false
                          },
                          zoom: {
                            enabled: false
                          },
                          events: {
                            dataPointSelection: function(event, chartContext, config) {
                              try {
                                if (config && config.dataPointIndex !== undefined && budget_by_service_head[config.dataPointIndex]) {
                                  const selectedServiceHead = budget_by_service_head[config.dataPointIndex]?.service_head_name
                                  if (selectedServiceHead) {
                                    handleCardClick('budget_service_head_rfqs', `Budget vs Final Amount Details for ${selectedServiceHead}`, { service_head_name: selectedServiceHead })
                                  }
                                }
                              } catch (error) {
                                console.error('Chart click error:', error)
                              }
                            }
                          }
                        },
                        plotOptions: {
                          bar: {
                            horizontal: false,
                            columnWidth: '55%',
                            endingShape: 'rounded'
                          }
                        },
                        dataLabels: {
                          enabled: false
                        },
                        stroke: {
                          show: true,
                          width: 2,
                          colors: ['transparent']
                        },
                        xaxis: {
                          categories: budget_by_service_head.map(item => item.service_head_name || 'Unknown'),
                          labels: {
                            rotate: 0,
                            style: {
                              fontSize: '11px',
                              fontFamily: 'inherit',
                              fontWeight: 500,
                              colors: '#74788d'
                            },
                            maxHeight: 60,
                            trim: true,
                            hideOverlappingLabels: true
                          },

                        },
                        yaxis: {
                          title: {
                            text: 'Amount (₹)',
                            style: {
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              fontWeight: 600,
                              color: '#495057'
                            }
                          },
                          labels: {
                            style: {
                              fontSize: '12px',
                              fontFamily: 'inherit',
                              colors: '#74788d'
                            },
                            formatter: function(val) {
                              return formatCurrency(val)
                            }
                          }
                        },
                        fill: {
                          opacity: 1
                        },
                        tooltip: {
                          shared: true,
                          intersect: false,
                          style: {
                            fontSize: '14px',
                            fontFamily: 'inherit'
                          },
                          y: {
                            formatter: function(val, opts) {
                              const serviceHeadData = budget_by_service_head[opts.dataPointIndex]

                              // Show only data without duplicate service head name
                              return `<div style="padding: 6px;">
                                <div style="font-size: 12px;">
                                  <span style="color: #556ee6;">●</span> ${formatCurrency(val)}<br/>
                                  <span style="color: #34c38f;">●</span> ${serviceHeadData?.rfq_count || 0} RFQs
                                </div>
                              </div>`
                            }
                          }
                        },
                        colors: ['#556ee6', '#34c38f'],
                        legend: {
                          position: 'top',
                          fontSize: '14px',
                          fontFamily: 'inherit',
                          fontWeight: 500,
                          markers: {
                            width: 12,
                            height: 12,
                            radius: 6
                          }
                        },
                        grid: {
                          borderColor: '#f1f1f1',
                          strokeDashArray: 3
                        },
                        responsive: [{
                          breakpoint: 768,
                          options: {
                            chart: {
                              height: 350
                            },
                            xaxis: {
                              labels: {
                                rotate: -45,
                                fontSize: '10px'
                              }
                            }
                          }
                        }]
                      }}
                      series={[
                        {
                          name: 'Budget',
                          data: budget_by_service_head.map(item => parseFloat(item.total_budget) || 0)
                        },
                        {
                          name: 'Final Amount',
                          data: budget_by_service_head.map(item => parseFloat(item.total_final_amount) || 0)
                        }
                      ]}
                      type="bar"
                      height={400}
                    />
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="bx bx-trending-up display-4 text-muted"></i>
                    <p className="text-muted mt-2">No service head budget vs final amount data available</p>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* Details Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setModalOpen(false)}>
          <i className="bx bx-detail me-2"></i>
          {modalTitle}
        </ModalHeader>
        <ModalBody>
          {/* Search and Controls - Always visible like Overview tab */}
          <Row className="mb-3">
            <Col md={4}>
              <InputGroup>
                <InputGroupText>
                  <i className="bx bx-search"></i>
                </InputGroupText>
                <Input
                  type="text"
                  placeholder="Search..."
                  value={modalSearch}
                  onChange={(e) => handleModalSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <div className="d-flex align-items-center">
                <label className="me-2">Show:</label>
                <Input
                  type="select"
                  value={modalPerPage}
                  onChange={(e) => {
                    const newPerPage = parseInt(e.target.value)
                    console.log("Dropdown onChange triggered with value:", newPerPage)
                    console.log("Current modalPerPage before change:", modalPerPage)
                    handleModalPerPageChange(newPerPage)
                  }}
                  style={{ width: 'auto' }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
                <span className="ms-2">entries</span>
              </div>
            </Col>
            <Col md={3}></Col>
            <Col md={2} className="text-end">
              <Button
                color="success"
                onClick={handleModalExport}
                disabled={exportLoading}
                block
              >
                {exportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="bx bx-download me-1"></i>
                    Export CSV
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* Data section with loading state */}
          {modalLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Loading details...</p>
            </div>
          ) : modalData && modalData.records ? (
            <>
              {/* Data Table */}
              <div className="table-responsive">
                <Table className="table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      {modalData.columns && modalData.columns.map((column, index) => (
                        <th key={index}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.records.map((record, index) => (
                      <tr key={index}>
                        {modalType === 'payment_service_head_rfqs' ? (
                          // Special handling for payment service head RFQs modal
                          <>
                            <td><strong>{record.rfq_code}</strong></td>
                            <td>
                              <div className="text-truncate" style={{maxWidth: '200px'}}>
                                {record.rfq_title}
                              </div>
                            </td>
                            <td>
                              <span className="text-truncate d-block" style={{maxWidth: '150px'}}>
                                {record.project_name}
                              </span>
                            </td>
                            <td>
                              <span className="text-truncate d-block" style={{maxWidth: '150px'}}>
                                {record.service_head}
                              </span>
                            </td>
                            <td className="text-end">
                              <strong>{formatCurrency(record.budget)}</strong>
                            </td>
                            <td className="text-end">
                              <strong>{formatCurrency(record.final_amount)}</strong>
                            </td>
                            <td className="text-end">
                              <strong>
                                {record.payment_amount !== undefined ?
                                  formatCurrency(record.payment_amount) :
                                  formatCurrency(record.pending_amount)
                                }
                              </strong>
                            </td>
                            <td>
                              <Badge color={
                                record.payment_status === 'paid' ? 'success' :
                                record.payment_status === 'partially paid' ? 'info' :
                                record.payment_status === 'pending' ? 'warning' : 'secondary'
                              }>
                                {record.payment_status === 'partially paid' ? 'Partially Paid' :
                                 record.payment_status.charAt(0).toUpperCase() + record.payment_status.slice(1)}
                              </Badge>
                            </td>
                            <td>
                              {record.payment_date || record.created_date}
                            </td>
                          </>
                        ) : modalType === 'head_rfqs' ? (
                          // Special handling for head RFQs modal
                          <>
                            <td><strong>{record.rfq_code}</strong></td>
                            <td>
                              <div className="text-truncate" style={{maxWidth: '200px'}}>
                                {record.rfq_title}
                              </div>
                            </td>
                            <td>
                              <span className="text-truncate d-block" style={{maxWidth: '150px'}}>
                                {record.project_name}
                              </span>
                            </td>
                            <td>
                              <span className="text-truncate d-block" style={{maxWidth: '150px'}}>
                                {record.service_subhead || 'N/A'}
                              </span>
                            </td>
                            <td className="text-end">
                              <strong>{formatCurrency(record.budget)}</strong>
                            </td>
                            <td className="text-end">
                              <strong>{formatCurrency(record.final_amount)}</strong>
                            </td>
                            <td>
                              {record.created_date}
                            </td>
                            <td>
                              {record.completion_date ? (
                                <small className="text-success">
                                  <i className="bx bx-check-circle me-1"></i>
                                  {record.completion_date}
                                </small>
                              ) : (
                                <small className="text-muted">-</small>
                              )}
                            </td>
                            <td>
                              <Badge color={
                                record.current_status === 'Closed' ? 'success' :
                                record.current_status === 'Assigned' ? 'info' :
                                record.current_status === 'In Progress' ? 'primary' : 'secondary'
                              }>
                                {record.current_status || 'Unknown'}
                              </Badge>
                            </td>
                          </>
                        ) : modalType === 'category_rfqs' ? (
                          // Special handling for category RFQs modal (no assignment status)
                          <>
                            <td><strong>{record.rfq_code}</strong></td>
                            <td>
                              <div className="text-truncate" style={{maxWidth: '200px'}}>
                                {record.rfq_title}
                              </div>
                            </td>
                            <td>
                              <span className="text-truncate d-block" style={{maxWidth: '150px'}}>
                                {record.project_name}
                              </span>
                            </td>
                            <td>
                              <span className="text-truncate d-block" style={{maxWidth: '120px'}}>
                                {record.category}
                              </span>
                            </td>
                            <td>
                              <span className="text-truncate d-block" style={{maxWidth: '150px'}}>
                                {record.service_head}
                              </span>
                            </td>
                            <td>
                              <span className="text-truncate d-block" style={{maxWidth: '150px'}}>
                                {record.service_subhead || 'N/A'}
                              </span>
                            </td>
                            <td className="text-end">
                              <strong>{formatCurrency(record.budget)}</strong>
                            </td>
                            <td className="text-end">
                              <strong>{formatCurrency(record.final_amount)}</strong>
                            </td>
                            <td>
                              {record.created_date}
                            </td>
                            <td>
                              <Badge color={
                                record.status === 'Closed' ? 'success' :
                                record.status === 'Work in Progress' ? 'primary' :
                                record.status === 'RFQ Assigned to Vendor' ? 'info' :
                                record.status === 'Vendor Invited' ? 'warning' :
                                record.status === 'RFQ Created' ? 'secondary' : 'light'
                              }>
                                {record.status || 'Unknown'}
                              </Badge>
                            </td>
                          </>
                        ) : (
                          // Generic handling for other modal types
                          <>
                            {Object.entries(record).map(([key, value], cellIndex) => {
                              // Skip ID columns (don't display them)
                              if (key === 'service_head_id' || key === 'service_subhead_id') {
                                return null;
                              }

                              return (
                                <td key={cellIndex}>
                                  {typeof value === 'number' && cellIndex > 0 ?
                                    (value.toString().includes('.') ? formatCurrency(value) : value) :
                                    value
                                  }
                                </td>
                              );
                            })}

                            {/* Add View RFQ button for service heads modal */}
                            {modalType === 'service_heads' && (
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleViewRFQs(record.service_head_id, record.service_head)}
                                >
                                  <i className="bx bx-list-ul me-1"></i>
                                  View RFQs
                                </Button>
                              </td>
                            )}

                            {/* Add View RFQ button for service subheads modal */}
                            {modalType === 'service_subheads' && (
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleViewSubheadRFQs(record.service_subhead_id, record.service_subhead)}
                                >
                                  <i className="bx bx-list-ul me-1"></i>
                                  View RFQs
                                </Button>
                              </td>
                            )}
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {modalData.pagination && (
                <Row className="mt-3">
                  <Col md={6}>
                    <div className="d-flex align-items-center">
                      <span className="text-muted">
                        Showing {((modalData.pagination.current_page - 1) * modalData.pagination.per_page) + 1} to{' '}
                        {Math.min(modalData.pagination.current_page * modalData.pagination.per_page, modalData.pagination.total_records)} of{' '}
                        {modalData.pagination.total_records} entries
                      </span>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end">
                      <ButtonGroup>
                        <Button
                          color="light"
                          disabled={!modalData.pagination.has_prev_page}
                          onClick={() => handleModalPageChange(modalData.pagination.current_page - 1)}
                        >
                          <i className="bx bx-chevron-left"></i>
                        </Button>
                        {Array.from({ length: Math.min(5, modalData.pagination.total_pages) }, (_, i) => {
                          const page = modalData.pagination.current_page - 2 + i
                          if (page > 0 && page <= modalData.pagination.total_pages) {
                            return (
                              <Button
                                key={page}
                                color={page === modalData.pagination.current_page ? "primary" : "light"}
                                onClick={() => handleModalPageChange(page)}
                              >
                                {page}
                              </Button>
                            )
                          }
                          return null
                        })}
                        <Button
                          color="light"
                          disabled={!modalData.pagination.has_next_page}
                          onClick={() => handleModalPageChange(modalData.pagination.current_page + 1)}
                        >
                          <i className="bx bx-chevron-right"></i>
                        </Button>
                      </ButtonGroup>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <i className="bx bx-info-circle display-4 text-muted"></i>
              <p className="text-muted mt-2">No data available</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* RFQ List Modal */}
      <Modal isOpen={rfqModalOpen} toggle={() => setRfqModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setRfqModalOpen(false)}>
          <i className="bx bx-list-ul me-2"></i>
          {rfqModalTitle}
        </ModalHeader>
        <ModalBody>
          {/* Search and Controls - Always visible like Overview tab */}
          <Row className="mb-3">
            <Col md={4}>
              <InputGroup>
                <InputGroupText>
                  <i className="bx bx-search"></i>
                </InputGroupText>
                <Input
                  type="text"
                  placeholder="Search RFQs..."
                  value={rfqModalSearch}
                  onChange={(e) => handleRfqModalSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <div className="d-flex align-items-center">
                <label className="me-2">Show:</label>
                <Input
                  type="select"
                  value={rfqModalPerPage}
                  onChange={(e) => handleRfqModalPerPageChange(parseInt(e.target.value))}
                  style={{ width: 'auto' }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
                <span className="ms-2">entries</span>
              </div>
            </Col>
            <Col md={5} className="text-end">
              <small className="text-muted">
                Showing {((rfqModalData && rfqModalData.pagination) ? ((rfqModalData.pagination.current_page - 1) * rfqModalData.pagination.per_page) + 1 : 0)} to{' '}
                {(rfqModalData && rfqModalData.pagination) ? Math.min(rfqModalData.pagination.current_page * rfqModalData.pagination.per_page, rfqModalData.pagination.total_records) : 0} of{' '}
                {(rfqModalData && rfqModalData.pagination) ? rfqModalData.pagination.total_records : 0} entries
              </small>
            </Col>
          </Row>

          {/* Data section with loading state */}
          {rfqModalLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Loading RFQs...</p>
            </div>
          ) : rfqModalData && rfqModalData.records ? (
            <>
              {/* RFQ Data Table */}
              <div className="table-responsive">
                <Table className="table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      {rfqModalData.columns && rfqModalData.columns.map((column, index) => (
                        <th key={index}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rfqModalData.records.map((record, index) => (
                      <tr key={index}>
                        {Object.values(record).map((value, cellIndex) => (
                          <td key={cellIndex}>
                            {typeof value === 'number' && cellIndex === 6 ? // Budget column
                              formatCurrency(value) :
                              value
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* RFQ Pagination */}
              {rfqModalData.pagination && rfqModalData.pagination.total_pages > 1 && (
                <Row className="mt-3">
                  <Col>
                    <div className="d-flex justify-content-center">
                      <ButtonGroup>
                        <Button
                          color="light"
                          disabled={!rfqModalData.pagination.has_prev_page}
                          onClick={() => handleRfqModalPageChange(rfqModalData.pagination.current_page - 1)}
                        >
                          <i className="bx bx-chevron-left"></i>
                        </Button>

                        {Array.from({ length: Math.min(5, rfqModalData.pagination.total_pages) }, (_, i) => {
                          const pageNum = rfqModalData.pagination.current_page - 2 + i;
                          if (pageNum > 0 && pageNum <= rfqModalData.pagination.total_pages) {
                            return (
                              <Button
                                key={pageNum}
                                color={pageNum === rfqModalData.pagination.current_page ? "primary" : "light"}
                                onClick={() => handleRfqModalPageChange(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          }
                          return null;
                        })}

                        <Button
                          color="light"
                          disabled={!rfqModalData.pagination.has_next_page}
                          onClick={() => handleRfqModalPageChange(rfqModalData.pagination.current_page + 1)}
                        >
                          <i className="bx bx-chevron-right"></i>
                        </Button>
                      </ButtonGroup>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <i className="bx bx-info-circle display-4 text-muted"></i>
              <p className="text-muted mt-2">No RFQs found</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default GoodServiceAnalytics
